import type React from 'react';
import { FormattedMessage } from 'react-intl';

import OrderAwaitingPayment from '../../../components/orders-drawer/sections/order-awaiting-payment';
import { RestoreCancelled } from '../../../components/restore/restore-cancelled-button';
import { RestoreShipped } from '../../../components/restore/restore-shipped-button';
import { OrderDetailsCanvasHotkeys } from '../../../components/hotkeys/advanced-hotkeys/order-details-canvas-hotkeys';
import { OrderDetailsTabSection } from '../tab-section/section';
import {
  Cancelled,
  ConfigureShipment,
  CustomerCommunication,
  CustomsInformation,
  ImportedAsShipped,
  Notes,
  OtherShippingOptions,
  RateCardReadOnlyFulfillments,
  ShipmentActivity,
  ShipmentDetails,
  ShipmentItems,
} from '../fulfillment-plan/sections';
import { FulfillmentPlannerState } from '../fulfillment-plan/fulfillment-planner/fulfillment-planner-form-state';
import fulfillmentPlannerMessages from '../fulfillment-plan/fulfillment-planner/messages';
import customsMessages from '../fulfillment-plan/sections/customs-information/messages';
import configureShipmentMessages from '../fulfillment-plan/sections/configure-shipment/messages';
import { messages as osoMessages } from '../../other-shipping-options/component';
import { InventoryStockLevelsProvider } from '../context/inventory-stock-levels-context';
import { FraudIndicatorsSection } from '../fraud-indicators/fraud-indicators-section';
import type { SectionId } from './customize-layout-provider.d';
import { SECTION_IDS } from './customize-layout-provider';
import { messages } from './messages';
import { SectionPlaceholder } from './section-placeholder';
import style from './section-components.module.less';

type SectionRenderProps = {
  counter: number;
  fulfillmentPlanCount: number;
  fulfillmentPlanId: string;
  isCustomizeMode: boolean;
  salesOrderId: string;
};

type FulfillmentPlannerRenderProps = SectionRenderProps & {
  fulfillmentPlannerState: FulfillmentPlannerState;
  isImportedAsShipped: boolean;
  isReshipEnabled: boolean;
  userHasCorrectRoles: boolean;
};

type SectionRenderer = (props: SectionRenderProps) => React.ReactNode;
type FulfillmentPlannerSectionRenderer = (props: FulfillmentPlannerRenderProps) => React.ReactNode;

const BASIC_SECTION_RENDERERS: Record<string, SectionRenderer> = {
  [SECTION_IDS.fraudIndicators]: () => <FraudIndicatorsSection />,
  [SECTION_IDS.shipmentDetails]: ({ counter, fulfillmentPlanCount }) => <ShipmentDetails counter={counter} total={fulfillmentPlanCount} />,
  [SECTION_IDS.items]: ({ fulfillmentPlanId }) => (
    <InventoryStockLevelsProvider fulfillmentPlanId={fulfillmentPlanId}>
      <ShipmentItems />
    </InventoryStockLevelsProvider>
  ),
  [SECTION_IDS.notes]: () => <Notes />,
  [SECTION_IDS.customerComms]: () => <CustomerCommunication />,
  [SECTION_IDS.activity]: () => <ShipmentActivity />,
};

const renderCustomsSection: SectionRenderer = ({ isCustomizeMode }) => (
  <SectionPlaceholder isCustomizeMode={isCustomizeMode} sectionTitle={<FormattedMessage {...customsMessages.customsHeader} />}>
    <CustomsInformation />
  </SectionPlaceholder>
);

const renderRateCardReadOnlySection: FulfillmentPlannerSectionRenderer = ({
  fulfillmentPlanId,
  fulfillmentPlannerState,
  isCustomizeMode,
  isImportedAsShipped,
}) => (
  <SectionPlaceholder isCustomizeMode={isCustomizeMode} sectionTitle={<FormattedMessage {...messages.rateCardReadOnlySectionTitle} />}>
    <RateCardReadOnlyFulfillments
      className={style['rate-card-read-only-wrapper']}
      fulfillmentPlanId={fulfillmentPlanId}
      showFulfillmentDisplay={fulfillmentPlannerState === FulfillmentPlannerState.Readonly && !isImportedAsShipped}
    />
  </SectionPlaceholder>
);

const renderCswSection: FulfillmentPlannerSectionRenderer = ({
  fulfillmentPlannerState,
  isCustomizeMode,
  isImportedAsShipped,
  isReshipEnabled,
  userHasCorrectRoles,
  fulfillmentPlanId,
  salesOrderId,
}) => {
  const content = () => {
    if (userHasCorrectRoles && fulfillmentPlannerState === FulfillmentPlannerState.Editable) {
      return (
        <>
          <ConfigureShipment />
          <OrderDetailsCanvasHotkeys fulfillmentPlanId={fulfillmentPlanId} />
        </>
      );
    }

    if (isImportedAsShipped) {
      return isReshipEnabled ? (
        <ImportedAsShipped />
      ) : (
        <OrderDetailsTabSection ariaLabelMessageDescriptor={fulfillmentPlannerMessages.restoreShipmentAriaLabel}>
          <RestoreShipped fulfillmentPlanId={fulfillmentPlanId} />
        </OrderDetailsTabSection>
      );
    }

    if (fulfillmentPlannerState === FulfillmentPlannerState.AwaitingPayment) {
      return (
        <OrderDetailsTabSection ariaLabelMessageDescriptor={fulfillmentPlannerMessages.awaitingPaymentSectionAriaLabel}>
          <OrderAwaitingPayment salesOrderId={salesOrderId} />
        </OrderDetailsTabSection>
      );
    }

    if (fulfillmentPlannerState === FulfillmentPlannerState.Cancelled) {
      return isReshipEnabled ? (
        <Cancelled />
      ) : (
        <OrderDetailsTabSection ariaLabelMessageDescriptor={fulfillmentPlannerMessages.cancelledSectionAriaLabel}>
          <RestoreCancelled fulfillmentPlanId={fulfillmentPlanId} />
        </OrderDetailsTabSection>
      );
    }

    return null;
  };

  return (
    <SectionPlaceholder isCustomizeMode={isCustomizeMode} sectionTitle={<FormattedMessage {...configureShipmentMessages.sectionTitle} />}>
      {content()}
    </SectionPlaceholder>
  );
};

const renderOsoSection: FulfillmentPlannerSectionRenderer = ({
  fulfillmentPlannerState,
  isCustomizeMode,
  isImportedAsShipped,
  userHasCorrectRoles,
}) => {
  const content = () => {
    if (userHasCorrectRoles && fulfillmentPlannerState === FulfillmentPlannerState.Editable) {
      return <OtherShippingOptions />;
    }

    if (fulfillmentPlannerState === FulfillmentPlannerState.Readonly && !isImportedAsShipped) {
      return <OtherShippingOptions isReadOnly />;
    }

    return null;
  };

  return (
    <SectionPlaceholder isCustomizeMode={isCustomizeMode} sectionTitle={<FormattedMessage {...osoMessages.title} />}>
      {content()}
    </SectionPlaceholder>
  );
};

export const renderSection = (sectionId: SectionId, props: FulfillmentPlannerRenderProps): React.ReactNode => {
  const basicRenderer = BASIC_SECTION_RENDERERS[sectionId];
  if (basicRenderer) {
    return basicRenderer(props);
  }

  if (sectionId === SECTION_IDS.customs) {
    return renderCustomsSection(props);
  }

  if (sectionId === SECTION_IDS.rateCardReadOnly) {
    return renderRateCardReadOnlySection(props);
  }

  if (sectionId === SECTION_IDS.csw) {
    return renderCswSection(props);
  }

  if (sectionId === SECTION_IDS.oso) {
    return renderOsoSection(props);
  }

  return null;
};
